<template>
  <div class="checkout-page">
    <header class="checkout-header">
      <router-link to="/" class="checkout-logo">
        Walls <span>&amp;</span> Faces
      </router-link>
    </header>

    <main class="checkout-main">
      <router-link to="/" class="back-link">← Back to shop</router-link>

      <h1 class="checkout-title">Order Summary</h1>

      <p v-if="cart.loading && !cart.lines.length" class="checkout-status">Loading your cart…</p>

      <div v-else-if="!cart.lines.length" class="checkout-empty">
        <p>Your cart is empty.</p>
        <router-link to="/shop" class="btn btn--dark">Browse the Shop</router-link>
      </div>

      <template v-else>
        <ul class="checkout-lines">
          <li v-for="line in cart.lines" :key="line.id" class="checkout-line">
            <img
              v-if="line.merchandise.image"
              :src="line.merchandise.image.url"
              :alt="line.merchandise.image.altText || line.merchandise.product.title"
              class="checkout-line__img"
            />
            <div v-else class="checkout-line__img checkout-line__img--placeholder"></div>

            <div class="checkout-line__body">
              <p class="checkout-line__title">{{ line.merchandise.product.title }}</p>
              <p class="checkout-line__meta">Qty {{ line.quantity }}</p>
            </div>

            <span class="checkout-line__price">{{ formatMoney(line.cost.totalAmount) }}</span>
          </li>
        </ul>

        <div class="checkout-total">
          <span>Total</span>
          <span>{{ formatMoney(cart.cost?.totalAmount) }}</span>
        </div>

        <p class="checkout-note">
          You'll be taken to Shopify's secure checkout to complete your order.
          We'll email you a confirmation with tracking as soon as it ships.
        </p>

        <p v-if="cart.error" class="checkout-error">{{ cart.error }}</p>

        <button
          class="btn btn--dark checkout-pay-btn"
          :disabled="!cart.checkoutUrl || redirecting"
          @click="goToPayment"
        >
          {{ redirecting ? 'Redirecting…' : 'Continue to Checkout' }}
        </button>
      </template>
    </main>
  </div>
</template>

<script>
import { useCart } from '../shopify/cart'
import { formatMoney as toMoney } from '../shopify/money'

export default {
  name: 'CheckoutPage',
  setup() {
    const { state: cart, loadCart } = useCart()
    return { cart, loadCart }
  },
  data() {
    return { redirecting: false }
  },
  mounted() {
    this.loadCart()
  },
  methods: {
    formatMoney(cost) {
      if (!cost) return ''
      return toMoney(cost.amount, cost.currencyCode)
    },
    goToPayment() {
      if (!this.cart.checkoutUrl) return
      this.redirecting = true
      // Full navigation: payment happens on Shopify's hosted checkout, not in this app.
      window.location.href = this.cart.checkoutUrl
    },
  },
}
</script>

<style scoped>
.checkout-page {
  min-height: 100vh;
  background: var(--canvas);
  color: var(--ink);
}

.checkout-header {
  border-bottom: 1px solid var(--linen);
  padding: 2.4rem 4rem;
}

.checkout-logo {
  font-family: var(--font-display);
  font-size: 2.8rem;
  font-weight: 500;
  color: var(--ink);
}

.checkout-logo span { font-style: italic; color: var(--amber); }

.checkout-main {
  max-width: 64rem;
  margin: 0 auto;
  padding: 6rem 2.4rem 10rem;
}

.back-link {
  display: inline-block;
  font-size: 1.3rem;
  color: var(--stone);
  margin-bottom: 3.2rem;
}
.back-link:hover { color: var(--ink); }

.checkout-title {
  font-size: 2.8rem;
  font-weight: 400;
  letter-spacing: -0.02em;
  margin-bottom: 3.2rem;
}

.checkout-status,
.checkout-empty {
  font-size: 1.4rem;
  color: var(--stone);
}

.checkout-empty {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2rem;
}

.checkout-lines {
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  border-bottom: 1px solid var(--linen);
  padding-bottom: 2.4rem;
  margin-bottom: 2.4rem;
}

.checkout-line {
  display: flex;
  align-items: center;
  gap: 1.6rem;
}

.checkout-line__img {
  width: 7.2rem;
  height: 7.2rem;
  object-fit: contain;
  flex-shrink: 0;
  background: var(--paper);
}

.checkout-line__body {
  flex: 1;
}

.checkout-line__title {
  font-size: 1.5rem;
  margin-bottom: 0.4rem;
}

.checkout-line__meta {
  font-size: 1.3rem;
  color: var(--stone);
}

.checkout-line__price {
  font-size: 1.4rem;
}

.checkout-total {
  display: flex;
  justify-content: space-between;
  font-size: 1.8rem;
  font-weight: 500;
  margin-bottom: 2.4rem;
}

.checkout-note {
  font-size: 1.3rem;
  color: var(--stone);
  line-height: 1.6;
  margin-bottom: 2.4rem;
}

.checkout-error {
  color: #b3261e;
  font-size: 1.3rem;
  margin-bottom: 1.6rem;
}

.checkout-pay-btn {
  width: 100%;
}
.checkout-pay-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
